import createAuth0Client, { Auth0Client, Auth0ClientOptions, IdToken } from '@auth0/auth0-spa-js'
import React, { ReactNode, useEffect, useState } from 'react'

export interface AuthenticationData {
    idToken: string
    accessToken: string
    claims: IdToken | undefined
    tokenExpiration?: number
}

export interface AuthError {
    name: string
    message: string
}

export interface IAuth0Context {
    isAuthenticated: boolean
    loading: boolean
    error?: AuthError
    getAuthenticationData: () => Promise<AuthenticationData>
    logout: () => void
}

export const Auth0Context = React.createContext<IAuth0Context>({
    isAuthenticated: false,
    loading: false,
    getAuthenticationData: () => {
        throw new Error('Auth0Provider is not present in React tree')
    },
    logout: () => {
        throw new Error('Auth0Provider is not present in React tree')
    }
})

export const useAuth0 = () => React.useContext(Auth0Context)

const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname)

type Auth0ProviderProps = {
    children?: ReactNode
    localStorage?: boolean
    onRedirectCallback?: (appState?: unknown) => void
    renderErrorPage: (logout: () => void, error: AuthError) => ReactNode
} & Auth0ClientOptions

const RealAuth0Provider: React.FC<Auth0ProviderProps> = ({
    children,
    onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
    renderErrorPage,
    ...initOptions
}) => {
    const params = new URL(document.location.href).searchParams
    const initialError = params.get('error')
        ? { name: params.get('error') ?? '', message: params.get('error_description') ?? '' }
        : undefined

    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [authenticationError, setAuthenticationError] = useState<AuthError | undefined>(initialError)
    const [auth0Client, setAuth0] = useState<Auth0Client | undefined>(undefined)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const initAuth0 = async () => {
            try {
                const auth0FromHook = await createAuth0Client({
                    cacheLocation: 'localstorage',
                    ...initOptions
                })
                setAuth0(auth0FromHook)

                if (authenticationError) {
                    window.history.replaceState({}, document.title, window.location.pathname)
                    setIsAuthenticated(false)
                    setLoading(false)
                    return
                }

                const url = new URL(window.location.href)
                if (url.searchParams.has('code') && url.searchParams.has('state')) {
                    try {
                        const result = await auth0FromHook.handleRedirectCallback()
                        if (result.appState) {
                            window.history.replaceState({}, document.title, atob(result.appState))
                        } else {
                            window.history.replaceState({}, document.title, window.location.pathname)
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error(e)
                        window.history.replaceState({}, document.title, window.location.pathname)
                        setIsAuthenticated(false)
                        setLoading(false)
                        const err = e as AuthError
                        setAuthenticationError({ name: err.name, message: err.message })
                        return
                    }
                }

                const authed = await auth0FromHook.isAuthenticated()
                if (authed) {
                    setIsAuthenticated(true)
                } else {
                    await auth0FromHook.loginWithRedirect({ appState: btoa(window.location.pathname) })
                }
                setLoading(false)
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e)
                setIsAuthenticated(false)
                setLoading(false)
                const err = e as AuthError
                setAuthenticationError({ name: err.name, message: err.message })
            }
        }
        initAuth0()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const logout = () => {
        if (auth0Client) {
            auth0Client.logout({ returnTo: window.location.origin })
        }
    }

    if (authenticationError) {
        return <>{renderErrorPage(logout, authenticationError)}</>
    }

    return (
        <Auth0Context.Provider
            value={{
                isAuthenticated,
                loading,
                error: authenticationError,
                getAuthenticationData: async () => {
                    if (!auth0Client) {
                        throw new Error('auth0Client is undefined')
                    }
                    const accessToken = await auth0Client.getTokenSilently()
                    const claimData = await auth0Client.getIdTokenClaims()
                    if (!claimData) {
                        auth0Client.logout({ returnTo: window.location.origin })
                    }
                    return {
                        accessToken,
                        idToken: claimData?.__raw ?? '',
                        claims: claimData,
                        tokenExpiration: claimData?.exp
                    }
                },
                logout
            }}
        >
            {children}
        </Auth0Context.Provider>
    )
}

const CYPRESS_TOKEN_KEY = 'park-depot:storage_token'

const CypressAuth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => (
    <Auth0Context.Provider
        value={{
            isAuthenticated: true,
            loading: false,
            getAuthenticationData: async () => {
                let retry = 0
                while (retry < 50 && !window.localStorage.getItem(CYPRESS_TOKEN_KEY)) {
                    // eslint-disable-next-line no-console
                    console.log('Waiting for Cypress authentication data to become available')
                    // eslint-disable-next-line no-await-in-loop
                    await new Promise((resolve) => {
                        setTimeout(resolve, 100)
                    })
                    retry += 1
                }
                const raw = window.localStorage.getItem(CYPRESS_TOKEN_KEY)
                if (raw) {
                    return JSON.parse(raw) as AuthenticationData
                }
                throw new Error('Cypress authentication data is not available')
            },
            logout: () => {
                // eslint-disable-next-line no-console
                console.log('logout was called')
            }
        }}
    >
        {children}
    </Auth0Context.Provider>
)

export const Auth0Provider: React.FC<Auth0ProviderProps> =
    'Cypress' in window ? CypressAuth0Provider : RealAuth0Provider
